import { ThirdPartyAppCredentialsPopupDetails } from '@coach-bot/data-access/shared/interfaces';
import { SetCopilotNotificationFeedbackOutput } from '@coach-bot/data-access/shared/models';
import { BackgroundAction, DashboardAction, WindowEventType } from '@shared/extension/enums';
import { ApiRequest, ApiResponse } from '@shared/extension/interfaces';
import { getActiveTab } from '@shared/extension/utils/tabs';
import { getLearningApp } from '@shared/utils';
import { filter, take } from 'rxjs';
import { Services } from '../services';
import {
  getLocalStorage,
  getThirdPartyAppCredentialsPopupDetails,
  handleAuthStatusChanged,
  setLocalStorage,
} from '../utils';
import { SignInOpts } from '@aws-amplify/auth/lib-esm/types';

interface Request<T = any> {
  action: BackgroundAction;
  detail?: T;
  type?: WindowEventType;
}

type SendResponse = (data: unknown) => void;

export function startHandlingActions(services: Services): void {
  chrome.runtime.onMessage.addListener(function (request, sender, callback) {
    const sendResponse: SendResponse = getSendResponseMethod(callback, request);
    switch (request.action) {
      case BackgroundAction.ApiRequest: {
        handleApiRequestAction(request, services, sendResponse);
        return true;
      }
      case BackgroundAction.ClearApiRequestCache: {
        services.apiRequestService.clearApiRequestCache(request.detail as ApiRequest);
        return;
      }
      case BackgroundAction.ExtensionAuthStatusChanged: {
        handleAuthStatusChanged(services, request.detail.email);
        return;
      }
      case BackgroundAction.GetBackendUrl: {
        sendResponse({ backendUrl: services.commonStore.backendUrl$.value });
        return;
      }
      case BackgroundAction.GetCopilotNotificationConfigurations: {
        if (request.type === WindowEventType.FromContentScript) {
          sendResponse({
            id: request.id,
            copilotNotificationsConfigurations: services.commonStore.copilotNotificationConfigurations$.value,
          });
        }
        return;
      }
      case BackgroundAction.GetCopilotScripts: {
        sendResponse({ copilotScripts: services.commonStore.copilotScripts$.value });
        return;
      }
      case BackgroundAction.GetExtensionSignedInStatus: {
        sendResponse({ isSignedIn: services.commonStore.isSignedIn$.value });
        return;
      }
      case BackgroundAction.GetLatestDashboardMessage: {
        sendResponse({ latestDashboardMessage: services.commonStore.latestDashboardMessage$.value });
        return;
      }
      case BackgroundAction.GetLocalStorage: {
        const key: string = request.detail.key;
        getLocalStorage(key).then((value: string | undefined) => {
          sendResponse({ key, value });
        });
        return true;
      }
      case BackgroundAction.GetProfileEmail: {
        sendResponse({ profileEmail: services.commonStore.profileEmail$.value });
        return;
      }
      case BackgroundAction.GetThirdPartyAppCredentials: {
        handleGetThirdPartyAppCredentialsAction(request, services, sendResponse);
        return true;
      }
      case BackgroundAction.GetThirdPartyAppCredentialsPopupDetails: {
        handleGetThirdPartyAppCredentialsPopupDetails(request, sendResponse, services);
        return true;
      }
      case BackgroundAction.GetUserDetails: {
        handleGetUserDetailsAction(services, sendResponse);
        return true;
      }
      case BackgroundAction.LoadRecommendations: {
        services.studentState.loadRecommendations();
        return;
      }
      case BackgroundAction.SignIn: {
        services.authService.signIn(request.detail as SignInOpts, false);
        return;
      }
      case BackgroundAction.SignOut: {
        services.authService.signOut(false);
        return;
      }
      case BackgroundAction.NavigateToUrl: {
        handleNavigateToUrlAction(request);
        return;
      }
      case BackgroundAction.Ping: {
        sendResponse({ isActive: true });
        return;
      }
      case BackgroundAction.SendCopilotNotificationFeedback: {
        services.copilotScriptsService
          .sendCopilotNotificationFeedback(request.detail.feedback)
          .subscribe((response: SetCopilotNotificationFeedbackOutput) => {
            sendResponse(response);
          });
        return true;
      }
      case BackgroundAction.SetLocalStorage: {
        const { key, value } = request.detail;
        setLocalStorage(key, value).then(() => {
          sendResponse({ key });
        });
        return true;
      }
      case BackgroundAction.UpdateIsExtensionPopupOpened: {
        services.commonStore.setIsExtensionPopupOpened(request.detail.isOpened);
        if (request.detail.isOpened) {
          services.serviceWorkerSubscriptionsService.resetCheckIsExtensionPopupOpenedInterval();
        }
        return;
      }
      case BackgroundAction.UpdateUserDetails: {
        if (!!request.detail.userDetails) {
          services.commonStore.setUserDetails(request.detail.userDetails);
        }
        return;
      }
      case BackgroundAction.TrackAnalyticsEvent: {
        handleTrackAnalyticsEventAction(request, services);
        return;
      }
    }
  });
}

/**
 * Handlers
 */

function handleApiRequestAction(request: Request, services: Services, sendResponse: SendResponse): void {
  const { apiRequest, cacheResponse } = request.detail;
  services.apiRequestService.sendApiRequest(apiRequest, cacheResponse).then(async (response: ApiResponse) => {
    sendResponse({ ...response });
  });
}

function handleGetThirdPartyAppCredentialsAction(
  request: Request,
  services: Services,
  sendResponse: SendResponse
): void {
  const appName: string = request.detail.appName;
  services.studentStore.thirdPartyAppCredentialsList$
    .pipe(
      filter(list => !!list),
      take(1)
    )
    .subscribe(thirdPartyAppCredentialsList => {
      sendResponse({
        credentials: thirdPartyAppCredentialsList.find(credentials => credentials.appName === appName) || null,
      });
    });
}

function handleGetThirdPartyAppCredentialsPopupDetails(
  request: Request,
  sendResponse: SendResponse,
  services: Services
): void {
  const appName: string = getLearningApp(request.detail.url);
  getThirdPartyAppCredentialsPopupDetails(appName, true, services).then(
    (popupDetails: ThirdPartyAppCredentialsPopupDetails) => {
      sendResponse({ popupDetails });
    }
  );
}

function handleGetUserDetailsAction(services: Services, sendResponse: SendResponse): void {
  services.commonStore.userDetailsLoaded$
    .pipe(
      filter((loaded: boolean) => loaded),
      take(1)
    )
    .subscribe(() => {
      sendResponse({ userDetails: services.commonStore.userDetails$.value });
    });
}

async function handleNavigateToUrlAction(request: Request): Promise<void> {
  if (request.detail && request.detail.url) {
    const newUrl = new URL(request.detail.url);
    const activeTab: chrome.tabs.Tab | null = await getActiveTab();
    if (!!activeTab && new URL(activeTab.url).hostname === newUrl.hostname) {
      chrome.tabs.update(activeTab.id, { url: request.detail.url });
    } else {
      chrome.tabs.create({ url: request.detail.url, active: true });
    }
  }
}

function handleTrackAnalyticsEventAction(request: Request, services: Services): void {
  services.analyticsService.track(request.detail.event, {
    email: services.commonStore.profileEmail$.value,
    userId: services.commonStore.userId$.value || undefined,
    studentId: services.commonStore.studentId$.value || undefined,
    ...request.detail.properties,
  });
}

/**
 * Utils
 */

function getSendResponseMethod(callback: (response: unknown) => void, request: Request): SendResponse {
  return function (data: unknown): void {
    try {
      callback(data);
    } catch (ex) {
      console.log(`Error sending response for action ${request.action}`, ex);
    }
  };
}
